# ImageTagsDialog.vue
<template>
  <v-dialog v-model="show" max-width="1150px" scrollable @click:outside="closeDialog">
    <v-card class="deploy-dialog-card">
      <v-card-title class="subtitle-1 fixed-header">
        <v-icon left small>mdi-rocket-launch-outline</v-icon>
        Deploy: {{ imageName }}
        <v-chip v-if="currentTag" x-small label color="success" class="ml-3">
          <v-icon left x-small>mdi-check-circle</v-icon>
          Current {{ currentTag }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-tabs v-model="tab" grow class="fixed-tabs">
        <v-tab><v-icon left small>mdi-source-branch</v-icon> Source &amp; Release</v-tab>
        <v-tab><v-icon left small>mdi-tag-multiple</v-icon> Image Tags</v-tab>
      </v-tabs>

      <v-card-text class="scrollable-content">
        <v-tabs-items v-model="tab">
          <!-- ============ Tab 0: Source & Release ============ -->
          <v-tab-item>
            <div class="pt-4">
              <v-alert v-if="projectError" type="info" text dense border="left">
                <div class="font-weight-medium">Not an SCM-managed deployment (read-only)</div>
                <div class="caption">
                  No GitLab project you can access is linked to
                  <span class="mono">{{ registryImageName }}</span> — SCM can't build or deploy this image.
                </div>
              </v-alert>
              <v-skeleton-loader v-else-if="projectLoading" type="table"></v-skeleton-loader>

              <template v-else-if="project">
                <v-alert v-if="!canDeploy" type="info" text dense border="left" class="mb-3">
                  <v-icon left small>mdi-eye-outline</v-icon>
                  Read-only — you can view commits and tags, but don't have permission to deploy this service.
                </v-alert>
                <div class="d-flex align-center mb-3">
                  <v-select v-model="selectedBranch" :items="branchNames" label="Branch" dense outlined hide-details
                            prepend-inner-icon="mdi-source-branch" style="max-width: 320px" @change="onBranchChange"></v-select>
                  <v-chip v-if="selectedBranchProtected" x-small color="success" outlined class="ml-3">
                    <v-icon left x-small>mdi-shield-check</v-icon> protected
                  </v-chip>
                  <v-spacer></v-spacer>
                  <v-btn x-small text :href="project.webUrl + '/-/commits/' + selectedBranch" target="_blank">
                    GitLab <v-icon x-small right>mdi-open-in-new</v-icon>
                  </v-btn>
                  <v-btn x-small icon class="ml-1" @click="reloadSource" :loading="commitsLoading"><v-icon x-small>mdi-refresh</v-icon></v-btn>
                </div>

                <v-simple-table dense class="commit-table">
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th style="width:104px">Commit</th>
                      <th>Message</th>
                      <th style="width:142px">Time</th>
                      <th style="width:140px">Author</th>
                      <th style="width:178px">Tag</th>
                      <th class="text-center" style="width:110px">Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in commitRows" :key="row.commit.id" :class="{ 'active-row': release.ref === row.commit.id, 'current-row': row.state.code === 'CURRENT' }">
                      <!-- Commit hash -->
                      <td>
                        <a :href="row.commit.webUrl || (project.webUrl + '/-/commit/' + row.commit.id)" target="_blank" class="text-decoration-none mono">
                          {{ commitShort(row.commit) }}
                        </a>
                      </td>

                      <!-- Message — 클릭 시 전체 내용 펼침 (호버 툴팁 제거) -->
                      <td :class="{ 'msg-open': isMsgOpen(row.commit.id) }">
                        <div class="commit-msg" @click="toggleMsg(row.commit.id)">{{ isMsgOpen(row.commit.id) ? commitFullMessage(row.commit) : commitTitle(row.commit) }}</div>
                      </td>

                      <!-- Time -->
                      <td><span class="caption grey--text">{{ formatDate(commitDate(row.commit)) }}</span></td>

                      <!-- Author -->
                      <td class="caption grey--text text--darken-1">
                        <v-icon x-small class="mr-1">mdi-account-outline</v-icon>{{ commitAuthor(row.commit) }}
                      </td>

                      <!-- Tag(s) — 커밋당 여러 개 가능 -->
                      <td>
                        <template v-if="!row.state.tags.length">
                          <v-tooltip bottom v-if="canDeploy">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn icon x-small v-bind="attrs" v-on="on" :disabled="releaseBusy" @click="createTagClick(row.commit)">
                                <v-icon small color="grey">mdi-tag-plus-outline</v-icon>
                              </v-btn>
                            </template>
                            <span>Add tag (optional — Deploy does this for you)</span>
                          </v-tooltip>
                          <span v-else class="grey--text caption">—</span>
                        </template>
                        <div v-else class="d-flex align-center py-1" style="gap:4px">
                          <v-chip v-for="t in row.state.tags" :key="t.name" x-small label :color="tagChipColor(t)"
                                  :outlined="!t.inNexus" :text-color="t.inNexus ? 'white' : ''">
                            <v-icon left x-small>{{ tagIcon(t) }}</v-icon>{{ t.name }}
                          </v-chip>
                          <v-tooltip bottom v-if="canDeploy">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn icon x-small v-bind="attrs" v-on="on" :disabled="releaseBusy" @click="createTagClick(row.commit)">
                                <v-icon x-small color="grey">mdi-tag-plus-outline</v-icon>
                              </v-btn>
                            </template>
                            <span>Add another tag</span>
                          </v-tooltip>
                        </div>
                      </td>

                      <!-- Action -->
                      <td class="text-center">
                        <!-- current: 배포된 커밋 — Deploy 와 동일 버튼 형태, 비활성 -->
                        <v-btn v-if="row.state.code === 'CURRENT'" x-small outlined color="success" disabled class="action-btn">
                          <v-icon left x-small>mdi-check</v-icon> current
                        </v-btn>

                        <v-btn v-else-if="row.state.code === 'BUILDING'" x-small text disabled class="action-btn">building…</v-btn>

                        <template v-else-if="row.state.code === 'FAILED'">
                          <v-tooltip bottom v-if="canDeploy">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn x-small icon color="warning" v-bind="attrs" v-on="on"
                                     :disabled="releaseBusy || !statePipe(row.state)" @click="retryCommit(row.commit)">
                                <v-icon small>mdi-restart</v-icon>
                              </v-btn>
                            </template>
                            <span>Retry pipeline (runner/infra failure). A code error needs a new commit.</span>
                          </v-tooltip>
                          <v-btn v-if="statePipe(row.state)" x-small icon :href="pipeUrl(statePipe(row.state))" target="_blank">
                            <v-icon small>mdi-open-in-new</v-icon>
                          </v-btn>
                        </template>

                        <!-- deploy / deploying — 스피너 없이 텍스트만(하단 모니터에서 진행 표시), 버튼 크기 동일 -->
                        <v-btn v-else-if="canDeploy" x-small color="primary" class="action-btn"
                               :disabled="releaseBusy"
                               @click="deployCommit(row.commit)">
                          <template v-if="releaseBusy && release.mode === 'deploy' && release.ref === row.commit.id">deploying…</template>
                          <template v-else><v-icon left x-small>mdi-rocket-launch</v-icon> Deploy</template>
                        </v-btn>
                        <span v-else class="grey--text caption">—</span>
                      </td>
                    </tr>
                    <tr v-if="!commits.length && !commitsLoading">
                      <td colspan="6" class="text-center grey--text py-4">No commits</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <div class="d-flex align-center justify-end mt-2">
                  <span class="caption grey--text mr-3">page {{ commitsPage }}</span>
                  <v-btn x-small icon :disabled="commitsPage <= 1 || commitsLoading" @click="loadCommits(commitsPage - 1)"><v-icon small>mdi-chevron-left</v-icon></v-btn>
                  <v-btn x-small icon :disabled="!hasMoreCommits || commitsLoading" @click="loadCommits(commitsPage + 1)"><v-icon small>mdi-chevron-right</v-icon></v-btn>
                </div>
              </template>
            </div>
          </v-tab-item>

          <!-- ============ Tab 1: Image Tags ============ -->
          <v-tab-item>
            <div class="pt-4">
              <v-skeleton-loader v-if="loading" type="article"></v-skeleton-loader>
              <template v-else-if="imageInfo">
                <v-card outlined class="mb-4">
                  <v-card-text>
                    <v-chip small label class="mr-2"><v-icon left small>mdi-chip</v-icon>{{ imageInfo.architecture }}</v-chip>
                    <v-chip small label><v-icon left small>mdi-desktop-classic</v-icon>{{ imageInfo.os }}</v-chip>
                  </v-card-text>
                </v-card>
                <v-data-table :headers="headers" :items="imageInfo.tags" dense item-key="uniqueId" @click:row="selectTag">
                  <template v-slot:item.tag="{ item }">
                    <div class="d-flex align-center">
                      <v-chip x-small :color="isCurrentTag(item) ? 'success' : ''" :outlined="!isCurrentTag(item)" class="mr-2">{{ item.tag }}</v-chip>
                      <v-chip v-if="item.isLatest" x-small color="blue" outlined>latest</v-chip>
                    </div>
                  </template>
                  <template v-slot:item.digest="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">{{ item.digest.substring(0, 15) }}...</span></template>
                      <span>{{ item.digest }}</span>
                    </v-tooltip>
                  </template>
                  <template v-slot:item.created="{ item }">{{ formatDate(item.created) }}</template>
                  <template v-slot:item.size="{ item }">{{ formatBytes(item.size) }}</template>
                  <template v-slot:item.env="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }"><v-btn x-small icon v-bind="attrs" v-on="on"><v-icon x-small>mdi-information</v-icon></v-btn></template>
                      <div><div v-for="(value, key) in item.env" :key="key"><strong>{{ key }}:</strong> {{ value }}</div></div>
                    </v-tooltip>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn v-if="canDeploy" x-small color="primary" @click.stop="selectTag(item)" :disabled="isCurrentTag(item)">{{ isCurrentTag(item) ? 'Current' : 'Deploy' }}</v-btn>
                    <span v-else class="grey--text caption">—</span>
                  </template>
                </v-data-table>
              </template>
              <v-alert v-else type="error" text dense>Failed to load image information</v-alert>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <!-- ============ shared release / deploy monitor (부드럽게 펼쳐짐) ============ -->
        <v-expand-transition>
        <div v-if="release.active" class="mt-4">
          <v-divider class="mb-3"></v-divider>
          <v-stepper alt-labels flat class="release-stepper mb-3" :value="monitorStep">
            <v-stepper-header>
              <v-stepper-step :complete="stepDone(1)" step="1"
                              :color="release.skipped ? 'grey lighten-1' : 'primary'"
                              :complete-icon="release.skipped ? 'mdi-minus' : '$complete'">
                Create Tag
                <small v-if="release.skipped" class="grey--text mt-1">skipped</small>
                <small v-else-if="release.tagName" class="mono mt-1">{{ release.tagName }}</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="stepDone(2)" :rules="[() => release.pipelineStatus !== 'failed']" step="2"
                              :color="release.skipped ? 'grey lighten-1' : 'primary'"
                              :complete-icon="release.skipped ? 'mdi-minus' : '$complete'">
                Pipeline
                <small v-if="release.skipped" class="grey--text mt-1">skipped</small>
                <small v-else-if="release.mode === 'build' && release.tagCreated" class="mt-1">{{ release.pipelineStatus || 'pending' }} · {{ formatElapsed(release.elapsed) }}</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="stepDone(3)" step="3"
                              :color="release.skipped ? 'grey lighten-1' : 'primary'"
                              :complete-icon="release.skipped ? 'mdi-minus' : '$complete'">
                Nexus Upload
                <small v-if="release.skipped" class="grey--text mt-1">skipped</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="stepDone(4)" step="4">
                Deploy<small v-if="release.deployStatus" class="mt-1">{{ release.deployStatus === 'done' ? 'done' : release.deployState || 'starting' }}</small>
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>

          <v-card outlined>
            <v-card-text>
              <template v-if="release.mode === 'build'">
                <div class="d-flex align-center mb-1">
                  <v-chip small label :color="release.nexusReady ? 'success' : pipelineColor(release.pipelineStatus)" text-color="white" class="mr-3">{{ release.nexusReady ? 'image ready' : (release.pipelineStatus || 'pending') }}</v-chip>
                  <span class="mono mr-2">{{ release.tagName }}</span>
                  <span class="caption grey--text">elapsed {{ formatElapsed(release.elapsed) }}</span>
                  <v-spacer></v-spacer>
                  <v-btn v-if="release.pipelineUrl" x-small text color="primary" :href="release.pipelineUrl" target="_blank">
                    GitLab Pipeline <v-icon x-small right>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>
                <v-progress-linear v-if="!release.nexusReady && !isPipelineDone(release.pipelineStatus)" indeterminate color="primary" class="mb-2"></v-progress-linear>
                <div class="d-flex align-center">
                  <v-icon small :color="release.nexusReady ? 'success' : 'grey'" class="mr-1">{{ release.nexusReady ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
                  <span class="caption">Nexus artifact {{ release.nexusReady ? 'confirmed' : 'pending (uploaded after pipeline success)' }}</span>
                  <v-spacer></v-spacer>
                  <v-btn x-small text color="grey darken-1" class="mr-1" @click="cancelDialog = true"><v-icon left x-small>mdi-close</v-icon> Cancel</v-btn>
                  <v-btn v-if="canDeploy && release.pipelineStatus === 'failed'" x-small outlined color="error" @click="retryActiveRelease"><v-icon left x-small>mdi-restart</v-icon> Retry</v-btn>
                  <v-btn v-if="canDeploy && release.nexusReady && !release.autoDeploy" color="primary" small class="ml-2" @click="startDeploy(release.tagName, release.ref)">
                    <v-icon left small>mdi-rocket-launch</v-icon> Deploy This Tag
                  </v-btn>
                </div>
                <div v-if="jobTrace" class="mt-3">
                  <div class="caption grey--text mb-1">
                    <v-icon x-small class="mr-1">mdi-console-line</v-icon>pipeline log (tail)
                    <v-btn v-if="release.pipelineUrl" x-small text color="primary" class="ml-1" :href="release.pipelineUrl" target="_blank">full log<v-icon x-small right>mdi-open-in-new</v-icon></v-btn>
                  </div>
                  <pre class="job-trace">{{ jobTrace }}</pre>
                </div>
              </template>

              <template v-else>
                <div class="d-flex align-center mb-3">
                  <v-progress-circular v-if="release.deployStatus !== 'done'" indeterminate size="20" width="2" color="primary" class="mr-3"></v-progress-circular>
                  <v-icon v-else color="success" class="mr-3">mdi-check-circle</v-icon>
                  <div>
                    <div class="subtitle-2">{{ release.deployStatus === 'done' ? 'Deployed' : 'Deploying' }} <span class="mono ml-1">{{ release.tagName }}</span></div>
                    <div class="caption grey--text">{{ release.deployStatus === 'done' ? 'Service updated and running the new image.' : 'Rolling update (start-first) in progress' }}</div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn v-if="releaseBusy" x-small outlined color="grey darken-1" class="mr-2" @click="cancelDialog = true"><v-icon left x-small>mdi-close</v-icon> Cancel</v-btn>
                  <v-btn v-if="release.deployStatus === 'done'" small text color="primary" @click="closeDialog">Close</v-btn>
                </div>
                <!-- 배포 세부단계: 이미지 체크 → pull → update → running -->
                <div class="deploy-phases d-flex align-center flex-wrap">
                  <template v-for="(p, i) in deployPhases">
                    <div :key="p.key" class="deploy-phase d-flex align-center">
                      <v-progress-circular v-if="deployPhaseState(i + 1) === 'current'" indeterminate size="16" width="2" color="primary" class="mr-1"></v-progress-circular>
                      <v-icon v-else :color="deployPhaseState(i + 1) === 'done' ? 'success' : 'grey lighten-1'" small class="mr-1">
                        {{ deployPhaseState(i + 1) === 'done' ? 'mdi-check-circle' : p.icon }}
                      </v-icon>
                      <span class="caption" :class="deployPhaseState(i + 1) === 'pending' ? 'grey--text text--lighten-1' : (deployPhaseState(i + 1) === 'current' ? 'primary--text font-weight-medium' : 'grey--text text--darken-1')">{{ p.label }}</span>
                    </div>
                    <v-icon :key="p.key + '-sep'" v-if="i < deployPhases.length - 1" small color="grey lighten-2" class="mx-2">mdi-chevron-right</v-icon>
                  </template>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <!-- Confirm image-tag deploy (Tab 1) -->
    <v-dialog v-model="confirmDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1">Confirm Deploy</v-card-title>
        <v-card-text>Deploy the service using image tag <strong>{{ selectedTag?.tag }}</strong>?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmUpdate">Deploy</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create deploy tag -->
    <v-dialog v-model="tagDialog" max-width="460">
      <v-card>
        <v-card-title class="subtitle-1">Create Deploy Tag</v-card-title>
        <v-card-text>
          <p class="caption grey--text mb-2">
            Tag commit <strong class="mono">{{ tagTargetShort }}</strong> on <strong class="mono">{{ selectedBranch }}</strong>.
            This triggers the CI pipeline to build and push the image to Nexus{{ deployAfterTag ? ', then deploys automatically.' : '.' }}
          </p>
          <v-text-field v-model="newTagName" label="Tag name" prepend-inner-icon="mdi-tag" dense outlined hide-details="auto" :rules="[v => !!v || 'Required']"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="tagDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="creatingTag" @click="confirmCreateTag">
            <v-icon left small>mdi-tag-plus</v-icon> {{ deployAfterTag ? 'Create, Build & Deploy' : 'Create & Build' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Choose which tag to deploy (commit has 2+ built tags in Nexus) -->
    <v-dialog v-model="tagChoiceDialog" max-width="440">
      <v-card>
        <v-card-title class="subtitle-1">Choose tag to deploy</v-card-title>
        <v-card-text>
          <p class="caption grey--text mb-2">This commit has more than one tag with a built image in Nexus. Pick which tag to deploy.</p>
          <v-list dense>
            <v-list-item v-for="name in tagChoices" :key="name" @click="chooseTagDeploy(name)">
              <v-list-item-icon><v-icon color="success">mdi-tag</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title class="mono">{{ name }}</v-list-item-title></v-list-item-content>
              <v-list-item-action><v-icon small color="primary">mdi-rocket-launch</v-icon></v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="tagChoiceDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel deploy/build monitoring (background job keeps running) -->
    <v-dialog v-model="cancelDialog" max-width="440">
      <v-card>
        <v-card-title class="subtitle-1">Cancel monitoring?</v-card-title>
        <v-card-text class="body-2">
          This stops the SCM monitor and re-enables the deploy buttons.
          <strong>The pipeline / deployment keeps running on the server</strong> — this does not abort it.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog = false">Keep watching</v-btn>
          <v-btn color="error" text @click="cancelRelease"><v-icon left small>mdi-close</v-icon> Cancel monitor</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import registryApi from '@/config/api/registry'
import gitlabApi from '@/config/api/gitlab'
import swarmApi from '@/config/api/swarm'

const POLL_MS = 8000        // 빌드 중 폴링 — GitLab/Nexus VM 이 빌드로 포화라 간격 넓혀 부하·타임아웃 완화
const DEPLOY_POLL_MS = 3000
const UPDATE_CONFIG = { parallelism: 1, delay: 10, failureAction: 'pause' }
const RUNNING_STATES = ['running', 'pending', 'created', 'preparing', 'waiting_for_resource', 'scheduled']

export default {
  name: 'ImageTagsDialog',

  props: {
    value: Boolean,
    service: { type: Object, required: true },
    clstNm: { type: String, default: 'demo' }
  },

  data: () => ({
    tab: 0,
    loading: false,
    imageInfo: null,
    selectedTag: null,
    confirmDialog: false,
    headers: [
      { text: 'Tag', value: 'tag', width: '120' },
      { text: 'Created', value: 'created', width: '180' },
      { text: 'Size', value: 'size', width: '100' },
      { text: 'Digest', value: 'digest' },
      { text: 'Env', value: 'env', width: '50', align: 'center' },
      { text: 'Actions', value: 'actions', width: '100', align: 'center', sortable: false }
    ],
    projectLoading: false,
    projectError: false,
    project: null,
    branchesData: [],
    pipelinesData: [],
    selectedBranch: '',
    commits: [],
    commitsPage: 1,
    commitsPerPage: 5,
    commitsLoading: false,
    hasMoreCommits: false,
    gitTags: [],
    nexusTagNames: [],
    tagDialog: false,
    tagTargetCommit: null,
    tagTargetRef: '',
    newTagName: '',
    deployAfterTag: false,
    creatingTag: false,
    tagChoiceDialog: false,
    tagChoiceCommit: null,
    tagChoices: [],
    release: {
      active: false, mode: '', tagName: '', ref: '', autoDeploy: false,
      startedAt: 0, elapsed: 0, tagCreated: false,
      pipelineStatus: null, pipelineUrl: '', pipelineId: null, nexusReady: false,
      deployStatus: null, deployState: '', skipped: false
    },
    cancelDialog: false,
    jobTrace: '',            // 빌드 중 파이프라인 잡 로그 tail
    justDeployedTag: null,   // 방금 배포한 태그 — service prop 갱신 전까지 current 즉시 반영용
    refreshedImage: null,    // ↻ 새로고침 시 재조회한 실제 배포 이미지 (prop 은 부모가 갱신 전까지 stale)
    deployPhases: [
      { key: 'check', label: 'Image check', icon: 'mdi-magnify' },
      { key: 'pull', label: 'Pull image', icon: 'mdi-tray-arrow-down' },
      { key: 'update', label: 'Update service', icon: 'mdi-cog-outline' },
      { key: 'run', label: 'Running', icon: 'mdi-play-circle-outline' }
    ],
    openMsgs: {},   // 커밋 메시지 펼침 상태 (id -> bool)
    pollTimer: null
  }),

  computed: {
    show: {
      get() { return this.value },
      set(value) { this.$emit('input', value); if (!value) this.$emit('close') }
    },
    svcImage() { return this.refreshedImage || this.service.Spec.TaskTemplate.ContainerSpec.Image },
    imageName() { return this.svcImage.split('@')[0] },
    baseImage() { return this.imageName.split(':')[0] },
    registryImageName() {
      const m = this.imageName.match(/^docker\.innilabs\.co\.id\/(.+?)(?::[^/]+)?$/)
      return m ? m[1] : ''
    },
    currentTag() {
      if (this.justDeployedTag) return this.justDeployedTag
      return this.svcImage.split(':')[1]?.split('@')[0] || 'latest'
    },
    gitlabToken() { return this.$store.getters.getUser?.gitlabAccessToken },
    serviceStack() { return this.service?.Spec?.Labels?.['com.docker.stack.namespace'] || '' },
    // 배포 액션 노출 여부 = admin(전체) 또는 user(demo 스택만). 백엔드가 최종 게이트.
    canDeploy() {
      const g = this.$store.getters
      return g.isAdmin || (g.isUser && g.deployableStacks.includes(this.serviceStack))
    },
    branchNames() { return (this.branchesData || []).map(b => b.name) },
    selectedBranchProtected() {
      const b = (this.branchesData || []).find(b => b.name === this.selectedBranch)
      return !!(b && b.protected)
    },
    commitRows() {
      return (this.commits || []).map(c => ({ commit: c, state: this.computeState(c) }))
    },
    tagTargetShort() {
      const c = this.tagTargetCommit
      return c ? (c.shortId || (c.id || '').substring(0, 8)) : ''
    },
    monitorStep() {
      if (this.release.deployStatus) return 4
      // Nexus 준비돼도 배포는 옵션(수동) — deploy 단계는 배포 시작 전까지 비활성 유지
      if (this.release.nexusReady || this.release.pipelineStatus === 'success') return 3
      if (this.release.tagCreated) return 2
      return 1
    },
    // 진행 중일 때만 true (배포 done 이면 false → 표 버튼 스피너 멈추고 재활성)
    releaseBusy() {
      const r = this.release
      return r.active && !(r.mode === 'deploy' && r.deployStatus === 'done')
    },
    // swarm task state → 배포 세부단계 인덱스 (1 스케줄 · 2 pull · 3 start · 4 running)
    deployPhaseIndex() {
      if (this.release.deployStatus === 'done') return 4
      const s = (this.release.deployState || '').toLowerCase()
      if (['running', 'complete'].includes(s)) return 4
      if (['starting', 'ready'].includes(s)) return 3
      if (['preparing'].includes(s)) return 2
      return 1
    }
  },

  methods: {
    closeDialog() { this.stopPolling(); this.resetRelease(); this.show = false },
    resetRelease() {
      this.release = {
        active: false, mode: '', tagName: '', ref: '', autoDeploy: false,
        startedAt: 0, elapsed: 0, tagCreated: false,
        pipelineStatus: null, pipelineUrl: '', pipelineId: null, nexusReady: false,
        deployStatus: null, deployState: '', skipped: false
      }
      this.jobTrace = ''
    },
    // 모니터만 중단 — 파이프라인/배포는 서버에서 계속 진행됨. 표 버튼 재활성.
    cancelRelease() {
      this.cancelDialog = false
      this.stopPolling()
      this.resetRelease()
    },
    // 새로고침/재오픈 시 서비스가 아직 rolling update 중이면 배포 모니터 이어받기 (재트리거 안 함)
    resumeIfDeploying() {
      if (this.release.active) return
      const us = this.service && this.service.UpdateStatus
      if (!us || !['updating', 'paused'].includes((us.State || '').toLowerCase())) return
      this.release = {
        active: true, mode: 'deploy', tagName: this.currentTag, ref: '', autoDeploy: false,
        startedAt: Date.parse(us.StartedAt) || Date.now(), elapsed: 0, tagCreated: true,
        pipelineStatus: 'success', pipelineUrl: '', pipelineId: null, nexusReady: true,
        deployStatus: 'deploying', deployState: '', skipped: true
      }
      this.startPolling()
    },
    // 새로고침/재오픈 시 진행 중인 빌드(태그에 running 파이프라인 + Nexus 아직 없음) 이어받기.
    // 빌드 state 는 GitLab 파이프라인이 SoT — 백엔드 저장 불필요, 열 때 live 조회로 복원.
    resumeIfBuilding() {
      if (this.release.active) return
      for (const t of (this.gitTags || [])) {
        if (this.nexusHas(t.name)) continue
        const pipe = this.pipelineForTag(t.name)
        if (pipe && RUNNING_STATES.includes(pipe.status)) {
          this.startBuildMonitor(t.name, { id: t.commit && t.commit.id }, false, true, pipe)
          return
        }
      }
    },
    deployPhaseState(idx) {   // idx 1-based
      if (this.release.deployStatus === 'done') return 'done'
      const cur = this.deployPhaseIndex
      if (cur > idx) return 'done'
      if (cur === idx) return 'current'
      return 'pending'
    },

    // ---------- Image tab ----------
    async loadImageInfo() {
      this.loading = true; this.imageInfo = null
      try {
        // silent: SCM 관리대상 아닌 이미지(test/* 등)는 Nexus 에 없어 실패 → error 토스트 대신 조용히 read-only 안내
        const response = await registryApi.getImageTags(this.registryImageName, { silent: true })
        this.nexusTagNames = (response?.tags || []).map(t => t.tag)
        if (response?.tags?.length > 0) this.imageInfo = { ...response, tags: this.processImageTags(response.tags) }
        else throw new Error('No tags found')
      } catch (error) {
        console.error('Failed to load image tags:', error); this.imageInfo = null
      } finally { this.loading = false }
    },
    processImageTags(tags) {
      const latestDigest = tags.find(t => t.tag === 'latest')?.digest
      return tags.filter(t => t.tag !== 'latest')
          .map((t, i) => ({ ...t, uniqueId: `${t.tag}-${i}`, isLatest: t.digest === latestDigest }))
          .sort((a, b) => new Date(b.created) - new Date(a.created))
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date); if (isNaN(d)) return ''
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    formatBytes(bytes) {
      if (!bytes) return 'N/A'
      const u = ['B', 'KB', 'MB', 'GB']; let v = bytes, i = 0
      while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
      return `${v.toFixed(1)} ${u[i]}`
    },
    selectTag(tag) { if (this.isCurrentTag(tag)) return; this.selectedTag = tag; this.confirmDialog = true },
    isCurrentTag(tag) { return tag.tag === this.currentTag },
    confirmUpdate() { if (!this.selectedTag) return; this.confirmDialog = false; this.startDeploy(this.selectedTag.tag, null) },

    // ---------- Source & Release ----------
    async resolveProject() {
      if (this.project || !this.registryImageName) return
      this.projectLoading = true; this.projectError = false
      try {
        const list = await gitlabApi.pjList({ token: this.gitlabToken })
        const want = this.registryImageName.toLowerCase()
        const match = (list || []).find(p => (p.pathWithNamespace || `${p.namespace?.fullPath}/${p.path}`).toLowerCase() === want)
        if (!match) { this.projectError = true; return }
        this.project = match
        await this.loadProjectMeta()
        this.selectedBranch = this.branchNames.includes(match.defaultBranch) ? match.defaultBranch
            : (this.branchNames.includes('main') ? 'main' : (this.branchNames[0] || ''))
        await Promise.all([this.loadCommits(1), this.loadTags(), this.refreshNexus()])
      } catch (error) {
        console.error('Failed to resolve GitLab project:', error); this.projectError = true
      } finally { this.projectLoading = false }
    },
    async loadProjectMeta(silent = false) {
      try {
        const det = await gitlabApi.projectDetails({ projectId: this.project.id, token: this.gitlabToken }, silent ? { silent: true } : undefined)
        this.branchesData = det?.branches || []
        this.pipelinesData = det?.pipelines || []
      } catch (e) { console.error('project meta load failed:', e) }
    },
    async loadCommits(page) {
      if (page < 1) return
      this.commitsLoading = true
      try {
        const res = await gitlabApi.commits({ projectId: this.project.id, ref: this.selectedBranch, page, perPage: this.commitsPerPage, token: this.gitlabToken })
        // 실제 커밋시각 기준 내림차순 (API 기본순서는 머지 시 topological 이라 시간순 아님)
        this.commits = (res || []).slice().sort((a, b) => new Date(this.commitDate(b)) - new Date(this.commitDate(a)))
        this.commitsPage = page
        this.hasMoreCommits = (res || []).length === this.commitsPerPage
      } catch (e) { console.error('commits load failed:', e); this.commits = [] } finally { this.commitsLoading = false }
    },
    async loadTags(silent = false) {
      try { this.gitTags = (await gitlabApi.tags({ projectId: this.project.id, token: this.gitlabToken }, silent ? { silent: true } : undefined)) || [] }
      catch (e) { console.error('tags load failed:', e) }
    },
    async refreshNexus(silent = false) {
      try {
        const res = await registryApi.getImageTags(this.registryImageName, silent ? { silent: true } : undefined)
        this.nexusTagNames = (res?.tags || []).map(t => t.tag)
      } catch (e) { console.error('nexus refresh failed:', e) }
    },
    onBranchChange() { this.loadCommits(1) },
    async reloadSource() {
      this.justDeployedTag = null
      await Promise.all([this.loadCommits(this.commitsPage), this.loadProjectMeta(), this.loadTags(), this.refreshNexus()])
      // 실제 배포된 이미지도 재조회 → current 갱신 (prop 은 부모 refresh 전까지 stale)
      try {
        const res = await swarmApi.inspectService({ clstNm: this.clstNm, serviceId: this.service.ID })
        const img = res?.service?.Spec?.TaskTemplate?.ContainerSpec?.Image
        if (img) this.refreshedImage = img
      } catch (e) { console.error('service inspect failed:', e) }
    },
    commitTitle(c) { return c.title || (c.message || '').split('\n')[0] || '' },
    commitFullMessage(c) { return c.message || c.title || '' },
    toggleMsg(id) { this.$set(this.openMsgs, id, !this.openMsgs[id]) },
    isMsgOpen(id) { return !!this.openMsgs[id] },
    commitShort(c) { return c.shortId || (c.id || '').substring(0, 8) },
    commitAuthor(c) { return c.authorName || c.committerName || '—' },
    commitDate(c) { return c.committedDate || c.createdAt || c.authoredDate || '' },
    tagsForCommit(commitId) {
      return (this.gitTags || [])
          .filter(t => (t.commit && t.commit.id) === commitId)
          .map(t => t.name)
          .sort().reverse()   // YYYY.MMDD.HHMM 태그명 → 사전순 역정렬 = 최신 태그 먼저
    },
    nexusHas(tagName) { return this.nexusTagNames.includes(tagName) },
    pipelineForTag(tagName) {
      return (this.pipelinesData || []).filter(p => p.ref === tagName)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null
    },
    // 커밋 하나에 태그 여러 개 가능. 각 태그를 상태로 확장, Nexus 이미지 존재 = 배포가능 SoT.
    computeState(c) {
      const names = this.tagsForCommit(c.id)
      if (!names.length) return { code: 'NO_TAG', tags: [], deployable: [] }
      const tags = names.map(name => {
        const inNexus = this.nexusHas(name)
        const pipe = this.pipelineForTag(name)
        let status
        if (inNexus) status = (this.currentTag === name ? 'current' : 'ready')
        else if (pipe && RUNNING_STATES.includes(pipe.status)) status = 'building'
        else if (pipe && (pipe.status === 'failed' || pipe.status === 'canceled')) status = 'failed'
        else status = 'pending'   // 파이프라인 아직 없음(태그 방금 생성) or 성공했으나 Nexus 반영 전 → 실패 아님
        return {
          name, inNexus, pipe, status,
          isCurrent: status === 'current',
          building: status === 'building' || status === 'pending',
          failed: status === 'failed'
        }
      })
      const deployable = tags.filter(t => t.inNexus)
      let code
      if (tags.some(t => t.status === 'current')) code = 'CURRENT'
      else if (deployable.length) code = 'READY'
      else if (tags.some(t => t.building)) code = 'BUILDING'
      else code = 'FAILED'
      return { code, tags, deployable }
    },
    tagChipColor(t) { return t.inNexus ? 'success' : (t.status === 'failed' ? 'error' : 'info') },
    tagIcon(t) {
      if (t.isCurrent) return 'mdi-check-decagram'
      if (t.inNexus) return 'mdi-check-circle'
      if (t.status === 'failed') return 'mdi-alert-circle'
      return 'mdi-timer-sand'   // building / pending
    },
    statePipe(s) { const t = (s.tags || []).find(t => t.pipe); return t ? t.pipe : null },
    pipeUrl(pipe) {
      return pipe ? (pipe.webUrl || `${this.project.webUrl}/-/pipelines/${pipe.id}`) : ''
    },

    // ---------- unified deploy / tag flow ----------
    deployCommit(c) {
      const s = this.computeState(c)
      if (s.code === 'NO_TAG') { this.openTagDialog(c, true); return }
      if (s.code === 'BUILDING') { const b = s.tags.find(t => t.building); this.startBuildMonitor(b.name, c, true, true, b.pipe); return }
      // READY: Nexus 에 이미지 있는 태그로 배포. 성공 태그 1개면 그거, 2개↑면 어떤 태그 쓸지 물어봄.
      if (s.deployable.length === 1) { this.startDeploy(s.deployable[0].name, c.id); return }
      this.openTagChoice(c, s.deployable)
    },
    openTagChoice(c, deployable) {
      this.tagChoiceCommit = c
      this.tagChoices = (deployable || []).map(t => t.name)
      this.tagChoiceDialog = true
    },
    chooseTagDeploy(name) {
      this.tagChoiceDialog = false
      this.startDeploy(name, this.tagChoiceCommit ? this.tagChoiceCommit.id : '')
    },
    retryCommit(c) {
      const s = this.computeState(c)
      const t = (s.tags || []).find(t => t.pipe)
      if (!t) return
      gitlabApi.retryPL({ projectId: this.project.id, pipelineId: t.pipe.id, token: this.gitlabToken })
          .then(() => this.startBuildMonitor(t.name, c, false, true, t.pipe))
          .catch(e => console.error('retry failed:', e))
    },
    createTagClick(c) { this.openTagDialog(c, false) },
    openTagDialog(c, deployAfter) {
      this.tagTargetCommit = c; this.tagTargetRef = c.id
      this.newTagName = this.suggestTagName(); this.deployAfterTag = deployAfter; this.tagDialog = true
    },
    suggestTagName() {
      const d = new Date(); const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}.${p(d.getMonth() + 1)}${p(d.getDate())}.${p(d.getHours())}${p(d.getMinutes())}`
    },
    async confirmCreateTag() {
      if (!this.newTagName || !this.tagTargetRef) return
      this.creatingTag = true
      try {
        await gitlabApi.addTag({ projectId: this.project.id, tagName: this.newTagName, ref: this.tagTargetRef, token: this.gitlabToken })
        this.tagDialog = false
        await this.loadTags()
        this.startBuildMonitor(this.newTagName, this.tagTargetCommit, this.deployAfterTag, true, null)
      } catch (error) { console.error('Failed to create deploy tag:', error) } finally { this.creatingTag = false }
    },
    startBuildMonitor(tagName, commit, autoDeploy, tagCreated, pipe) {
      this.jobTrace = ''
      this.release = {
        active: true, mode: 'build', tagName, ref: commit ? commit.id : '', autoDeploy,
        startedAt: Date.now(), elapsed: 0, tagCreated: !!tagCreated,
        pipelineStatus: pipe ? pipe.status : null, pipelineUrl: pipe ? (pipe.webUrl || '') : '',
        pipelineId: pipe ? pipe.id : null, nexusReady: false, deployStatus: null, deployState: '', skipped: false
      }
      this.startPolling()
    },
    startDeploy(tagName, ref) {
      if (this.release.active && this.release.mode === 'build') {
        // 빌드→배포 이어지는 흐름: 앞 단계는 실제로 완료된 것이므로 skip 아님 (green 유지)
        this.release.mode = 'deploy'; this.release.deployStatus = 'deploying'; this.release.deployState = ''; this.release.skipped = false
      } else {
        this.release = {
          active: true, mode: 'deploy', tagName, ref: ref || '', autoDeploy: false,
          startedAt: Date.now(), elapsed: 0, tagCreated: true,
          pipelineStatus: 'success', pipelineUrl: '', pipelineId: null, nexusReady: true,
          deployStatus: 'deploying', deployState: '', skipped: true   // 태그 기존 → 태그/빌드/nexus 단계 스킵
        }
      }
      const newImage = `${this.baseImage}:${tagName}`
      swarmApi.updateServiceImage({ clstNm: this.clstNm, serviceId: this.service.ID, newImage, updateConfig: UPDATE_CONFIG })
          .catch(e => console.error('deploy trigger failed:', e))
      this.startPolling()
    },

    startPolling() {
      this.stopPolling()
      const fn = this.release.mode === 'deploy' ? this.pollDeploy : this.pollBuild
      const interval = this.release.mode === 'deploy' ? DEPLOY_POLL_MS : POLL_MS
      this.pollTimer = setInterval(fn, interval)
      fn()
    },
    stopPolling() { if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null } },

    async pollBuild() {
      if (!this.release.active) return
      this.release.elapsed = Math.floor((Date.now() - this.release.startedAt) / 1000)
      await this.loadProjectMeta(true)
      const pipe = this.pipelineForTag(this.release.tagName)
      if (pipe) {
        this.release.pipelineStatus = pipe.status
        this.release.pipelineId = pipe.id
        this.release.pipelineUrl = pipe.webUrl || `${this.project.webUrl}/-/pipelines/${pipe.id}`
      }
      if (this.release.pipelineId && !this.release.nexusReady) {
        try {
          const raw = await gitlabApi.pipelineTrace({ projectId: this.project.id, pipelineId: this.release.pipelineId, token: this.gitlabToken }, { silent: true })
          this.jobTrace = this.cleanTrace(raw)
        } catch (e) { /* 일시적 실패는 무시, 다음 폴에서 갱신 */ }
      }
      await this.refreshNexus(true)
      await this.loadTags(true)
      if (this.release.pipelineStatus === 'success' && this.nexusHas(this.release.tagName)) this.release.nexusReady = true
      if (this.nexusHas(this.release.tagName)) this.release.nexusReady = true
      if (this.release.nexusReady) {
        if (this.release.autoDeploy) { this.startDeploy(this.release.tagName, this.release.ref); return }
        this.stopPolling()
      } else if (this.release.pipelineStatus === 'failed' || this.release.pipelineStatus === 'canceled') {
        this.stopPolling()
      }
    },
    async pollDeploy() {
      if (!this.release.active) return
      this.release.elapsed = Math.floor((Date.now() - this.release.startedAt) / 1000)
      try {
        const tasks = await swarmApi.listServiceTasks({ clstNm: this.clstNm, serviceId: this.service.ID }, { silent: true })
        const suffix = ':' + this.release.tagName
        // 새 이미지 태스크만 추적 — 기존 running 컨테이너 상태를 잡아 Running(4)부터 시작 후 Pull(2)로 역행하는 버그 방지.
        // 새 태스크가 아직 없으면 deployState='' → 세부단계 1(Image check/scheduling) 부터 정순 진행.
        const withNew = (tasks || []).filter(t => (t.Spec?.ContainerSpec?.Image || '').includes(suffix))
        const newest = withNew.slice()
            .sort((a, b) => new Date(b.Status?.Timestamp || 0) - new Date(a.Status?.Timestamp || 0))[0]
        this.release.deployState = newest ? (newest.Status?.State || '').toLowerCase() : ''
        const done = (tasks || []).some(t =>
            (t.DesiredState || '').toLowerCase() === 'running' &&
            (t.Status?.State || '').toLowerCase() === 'running' &&
            (t.Spec?.ContainerSpec?.Image || '').includes(suffix))
        if (done) { this.release.deployStatus = 'done'; this.justDeployedTag = this.release.tagName; this.stopPolling(); this.$emit('deployed') }
      } catch (e) { console.error('poll deploy failed:', e) }
    },
    retryActiveRelease() {
      if (!this.release.pipelineId) return
      gitlabApi.retryPL({ projectId: this.project.id, pipelineId: this.release.pipelineId, token: this.gitlabToken })
          .then(() => { this.release.pipelineStatus = 'running'; this.startPolling() })
          .catch(e => console.error('retry failed:', e))
    },

    stepDone(n) {
      const r = this.release
      if (n === 1) return r.tagCreated || r.mode === 'deploy'
      if (n === 2) return r.pipelineStatus === 'success' || r.nexusReady || r.mode === 'deploy'
      if (n === 3) return r.nexusReady || r.mode === 'deploy'
      return r.deployStatus === 'done'
    },
    pipelineColor(status) {
      return ({ success: 'success', failed: 'error', running: 'info', pending: 'warning', created: 'warning', canceled: 'grey' })[status] || 'grey'
    },
    isPipelineDone(status) { return ['success', 'failed', 'canceled', 'skipped'].includes(status) },
    formatElapsed(sec) {
      const m = Math.floor(sec / 60), s = sec % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    cleanTrace(t) {
      return (t || '')
        .replace(/\x1B\[[0-9;?]*[ -/]*[@-~]/g, '')      // ANSI CSI escape
        .replace(/section_(start|end):\d+:\S+/g, '')    // GitLab 섹션 마커
        .replace(/\r/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(v) {
        if (v && this.service) this.$nextTick(async () => {
          this.justDeployedTag = null; this.refreshedImage = null
          await this.loadImageInfo(); await this.resolveProject()
          this.resumeIfDeploying()
          if (!this.release.active) this.resumeIfBuilding()
        })
        else if (!v) { this.stopPolling(); this.resetRelease() }
      }
    }
  },

  beforeDestroy() { this.stopPolling() }
}
</script>

<style scoped>
.deploy-dialog-card { display: flex; flex-direction: column; max-height: 90vh; }
.fixed-header { position: sticky; top: 0; z-index: 2; background-color: #f5f5f5; border-bottom: 1px solid #e0e0e0; }
.fixed-tabs { flex: 0 0 auto; }
.scrollable-content { overflow-y: auto; flex-grow: 1; padding-top: 0 !important; }
.release-stepper { box-shadow: none; background: transparent; }
.active-row { background-color: rgba(25, 118, 210, 0.06); }
/* 배포 중인 커밋 행 전체 옅은 파랑, 현재 배포된 커밋 행 전체 옅은 초록 */
.current-row { background-color: rgba(76, 175, 80, 0.10); }
.current-row:hover { background-color: rgba(76, 175, 80, 0.16) !important; }
/* 커밋 테이블 — 열 너비 고정 + 셀 한 줄 고정(줄바꿈 금지) + 넘치면 ellipsis */
.commit-table ::v-deep table { table-layout: fixed; width: 100%; }
.commit-table ::v-deep td, .commit-table ::v-deep th { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.commit-msg { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
.commit-table ::v-deep td.msg-open { white-space: normal; overflow: visible; }
.msg-open .commit-msg { white-space: pre-wrap; overflow: visible; text-overflow: clip; word-break: break-word; }
/* Action 열 버튼 크기 통일 (deploy / current / building / deploying) */
.action-btn { min-width: 94px !important; }
/* 파이프라인 로그 tail — 터미널 느낌 */
.job-trace {
  background: #1e1e1e; color: #d4d4d4;
  font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 11px; line-height: 1.45;
  padding: 8px 10px; border-radius: 4px; margin: 0;
  max-height: 180px; overflow: auto; white-space: pre-wrap; word-break: break-all;
}
.deploy-phases { padding: 6px 4px; }
.deploy-phase { white-space: nowrap; }
.mono { font-family: 'Roboto Mono', monospace; font-size: 0.85em; }
</style>
